
import { ContactInformation } from './ContactInformation';
import { EntityDetails } from './entity/EntityDetails';
import { OperationalDetails } from './OperationalDetails';
import { PayrollSchedule } from './PayrollSchedule';
import { DocumentUpload } from './DocumentUpload';
import { PEOPreferences } from './PEOPreferences';
import { ReviewAndSubmit } from './ReviewAndSubmit';
import { InsuranceDetails } from './InsuranceDetails';
import { WorkersCompAssessment } from './WorkersCompAssessment';
import { MedicalQuestionnaire } from './insurance/MedicalQuestionnaire';
import { useIsMobile } from '@/hooks/use-mobile';
import type { Application } from '@/types/database.types';

interface ApplicationSectionProps {
  currentSection: number;
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => Promise<void>;
}

export const ApplicationSection = ({ currentSection, data, onSave }: ApplicationSectionProps) => {
  const isMobile = useIsMobile();
  
  // Add console logs to debug section rendering
  console.log('Rendering ApplicationSection with section:', currentSection);
  
  // Handler for the MedicalQuestionnaire component - convert to async
  const handleSaveField = async (field: string, value: any) => {
    await onSave({ [field]: value });
  };
  
  // Each section should logically group related fields
  switch (currentSection) {
    // Contact Information (Employee Contact Details)
    case 0:
      return <ContactInformation data={data} onSave={onSave} />;
    
    // Company Information (Entity Details)
    case 1:
      return <EntityDetails data={data} onSave={onSave} />;
    
    // Insurance Details (Employee Benefits)
    case 2:
      return <InsuranceDetails data={data} onSave={onSave} />;
    
    // Operational Details (Employee Count, Locations)
    case 3:
      return <OperationalDetails data={data} onSave={onSave} />;
    
    // Medical Questionnaire (Employee Health Assessment)
    case 4:
      return (
        <div className="space-y-6">
          <div className="glass-card border border-white/30 shadow-elevation-2 rounded-xl overflow-hidden p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">Employee Health Assessment</h2>
            <p className="text-muted-foreground mb-6">
              This information helps us match you with PEO providers that can best meet your organization's health insurance needs.
              All information is kept confidential and only used for coverage evaluation.
            </p>
            <MedicalQuestionnaire 
              data={data} 
              onSave={handleSaveField}
            />
          </div>
        </div>
      );
    
    // Workers Comp Assessment (Risk Factors)
    case 5:
      return <WorkersCompAssessment data={data} onSave={onSave} />;
    
    // Payroll Schedule (Payment Cycles)
    case 6:
      return <PayrollSchedule data={data} onSave={onSave} />;
    
    // Document Upload
    case 7:
      return <DocumentUpload data={data} onSave={onSave} />;
    
    // PEO Preferences
    case 8:
      return <PEOPreferences data={data} onSave={onSave} />;
    
    // Review and Submit
    case 9:
      return <ReviewAndSubmit data={data} onSave={onSave} />;
    
    default:
      return (
        <div className="text-center text-muted-foreground">
          <h3 className="text-xl font-medium">Section {currentSection + 1}</h3>
          <p className="mt-2">This section is under development</p>
        </div>
      );
  }
};
